import { EDocumentStatus } from '@if/core/types';
import { useMutation, useQuery, useQueryClient, } from '@tanstack/react-query';
import apiClient from './api-client';
import { uploadFiles } from './upload-files-api';
const SCOPE = 'credit-application';
const DETAIL = 'detail';
const SUPPLEMENTALLOAN = 'supplemental-loan';
export const keysFactory = {
    all: (financingProgram) => [{ scope: SCOPE, financingProgram }],
    allDetails: (financingProgram) => [{ scope: SCOPE, entity: DETAIL, financingProgram }],
    detail: (id, financingProgram) => [{ scope: SCOPE, entity: DETAIL, id, financingProgram }],
    supplementalLoan: (id, financingProgram) => [{ scope: SCOPE, entity: SUPPLEMENTALLOAN, id, financingProgram }],
};
const getCreditApplication = (financingProgram) => async ({ queryKey: [{ id }] }) => {
    const response = await apiClient.get(`/${financingProgram}/creditApplication/${encodeURIComponent(id)}`);
    return response.data;
};
export function useGetCreditApplicationById(creditApplicationId, financingProgram, shouldPoll = false, timer = 5000) {
    const { isFetching, data, refetch } = useQuery({
        queryKey: keysFactory.detail(creditApplicationId, financingProgram),
        queryFn: getCreditApplication(financingProgram),
        enabled: Boolean(creditApplicationId),
        refetchInterval: shouldPoll ? timer : undefined,
    });
    return [data ?? null, isFetching, refetch];
}
const getActiveCreditAppLinkedToCellPhoneOrEmail = (financingProgram) => async (dto) => {
    const response = await apiClient.post(`/${financingProgram}/creditApplication/Duplicates`, dto);
    return response.data;
};
export function useGetActiveCreditAppLinkedToCellPhoneOrEmail(financingProgram) {
    const { mutateAsync, isPending } = useMutation({
        mutationFn: getActiveCreditAppLinkedToCellPhoneOrEmail(financingProgram),
        onSuccess: (data) => {
            return data;
        },
    });
    return [mutateAsync, isPending];
}
const needHelp = (financingProgram) => async ({ creditApplicationId, ...dto }) => {
    const response = await apiClient.post(`${financingProgram}/creditApplication/${creditApplicationId}/NeedHelp`, dto);
    return response.data;
};
export function useNeedHelp(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, isSuccess, isError } = useMutation({
        mutationFn: needHelp(financingProgram),
        onSuccess: (data) => {
            queryClient.setQueryData(keysFactory.detail(data.id, financingProgram), data);
            return data;
        },
    });
    return [mutateAsync, isPending, isSuccess, isError];
}
const updateWorksheet = (financingProgram) => async (dto) => {
    const response = dto.create
        ? await apiClient.post(`${financingProgram}/creditApplication/${dto.creditApplicationId}/worksheet`, dto)
        : await apiClient.put(`${financingProgram}/creditApplication/${dto.creditApplicationId}/worksheet`, dto);
    return response.data;
};
export function useUpdateWorksheet(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: updateWorksheet(financingProgram),
        onSuccess: (data, variables) => {
            const updated = {
                ...queryClient.getQueryData(keysFactory.detail(variables.creditApplicationId, financingProgram)),
            };
            updated.worksheet = data;
            queryClient.setQueryData(keysFactory.detail(variables.creditApplicationId, financingProgram), updated);
            return data;
        },
    });
    return [mutateAsync, isPending, reset];
}
const submitHardHit = (financingProgram) => async (creditApp) => {
    const response = await apiClient.put(`${financingProgram}/creditApplication/${creditApp.id}/submit`, creditApp);
    return response.data;
};
export function useSubmitHardHit(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: submitHardHit(financingProgram),
        onSuccess: (data) => {
            queryClient.setQueryData(keysFactory.detail(data.id, financingProgram), data);
            return data;
        },
    });
    return [mutateAsync, isPending, reset];
}
export function useUploadFiles(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: (dto) => uploadFiles(financingProgram, dto),
        onSuccess: (data, variables) => {
            const updatedCreditApp = {
                ...queryClient.getQueryData(keysFactory.detail(data.creditApplicationId, financingProgram)),
            };
            const doc = {
                applicantType: variables.applicantType,
                status: EDocumentStatus.AwaitingApproval,
                subKey: variables.subKey,
                refusalReason: null,
                typeId: variables.typeId,
            };
            updatedCreditApp.documents = [...updatedCreditApp.documents];
            const existingDocIdx = updatedCreditApp.documents.findIndex((x) => x.typeId === variables.typeId && x.subKey === variables.subKey && x.applicantType === variables.applicantType);
            if (existingDocIdx > -1) {
                updatedCreditApp.documents[existingDocIdx] = doc;
            }
            else {
                updatedCreditApp.documents.push(doc);
            }
            queryClient.setQueryData(keysFactory.detail(data.creditApplicationId, financingProgram), updatedCreditApp);
            return data;
        },
    });
    return [mutateAsync, isPending, reset];
}
const cancelApplication = (financingProgram) => async (id) => {
    const response = await apiClient.put(`${financingProgram}/creditApplication/${id}/cancel`);
    return response.data;
};
export function useCancelApplication(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: cancelApplication(financingProgram),
        onSuccess: (data) => {
            queryClient.setQueryData(keysFactory.detail(data.id, financingProgram), data);
            return data;
        },
    });
    return [mutateAsync, isPending, reset];
}
const resetApplicantBankRequest = (financingProgram) => async (dto) => {
    const response = await apiClient.delete(`${financingProgram}/creditApplication/${dto.id}/bankrequest/${dto.applicantType}`);
    return response.data;
};
export function useResetApplicantBankRequest(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: resetApplicantBankRequest(financingProgram),
        onSuccess: (data) => {
            queryClient.setQueryData(keysFactory.detail(data.id, financingProgram), data);
            return data;
        },
    });
    return [mutateAsync, isPending, reset];
}
const continueWithComputedIncome = (financingProgram) => async (dto) => {
    const response = await apiClient.post(`${financingProgram}/creditApplication/${dto.id}/ContinueWithComputedIncome/${dto.applicantType}`);
    return response.data;
};
export function useContinueWithComputedIncome(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: continueWithComputedIncome(financingProgram),
        onSuccess: (data) => {
            queryClient.setQueryData(keysFactory.detail(data.id, financingProgram), data);
            return data;
        },
    });
    return [mutateAsync, isPending, reset];
}
const retryHardHit = (financingProgram) => async (id) => {
    const response = await apiClient.post(`${financingProgram}/creditApplication/${encodeURIComponent(id)}/RetryHardHit`);
    return response.data;
};
export function useRetryHardHit(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: retryHardHit(financingProgram),
        onSuccess: (data) => {
            queryClient.setQueryData(keysFactory.detail(data.id, financingProgram), data);
            return data;
        },
    });
    return [mutateAsync, isPending, reset];
}
const completeApplicantFlinks = (financingProgram) => async (dto) => {
    const response = await apiClient.post(`${financingProgram}/creditApplication/${dto.id}/bankrequest/${dto.externalServiceId}/complete`);
    return response.data;
};
export function useCompleteApplicantFlinks(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: completeApplicantFlinks(financingProgram),
        onSuccess: (data, variables) => {
            if (!data?.id)
                return;
            const updatedData = {
                ...queryClient.getQueryData(keysFactory.detail(variables.id, financingProgram)),
            };
            const indexRequiredStep = updatedData.requiredExternalSteps.findIndex((x) => x.id === data.id);
            if (indexRequiredStep >= 0)
                updatedData.requiredExternalSteps[indexRequiredStep] = data;
            queryClient.setQueryData(keysFactory.detail(variables.id, financingProgram), updatedData);
            return updatedData;
        },
    });
    return [mutateAsync, isPending, reset];
}
const skipApplicantBankRequest = (financingProgram) => async (dto) => {
    const response = await apiClient.post(`${financingProgram}/creditApplication/${dto.id}/skipbankaccountrequest/${dto.applicantType}`);
    return response.data;
};
export function useSkipApplicantBankRequest(financingProgram) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: skipApplicantBankRequest(financingProgram),
        onSuccess: (data) => {
            queryClient.setQueryData(keysFactory.detail(data.id, financingProgram), data);
            return data;
        },
    });
    return [mutateAsync, isPending, reset];
}
const getCanHaveSupplementalLoanDto = (financingProgram) => async ({ queryKey: [{ id }] }) => {
    const response = await apiClient.get(`/${financingProgram}/creditApplication/${encodeURIComponent(id)}/GetCanHaveSupplementalLoan`);
    return response.data;
};
export function useGetCanHaveSupplementalLoanDto(creditApplicationId, financingProgram) {
    const { isFetching, data } = useQuery({
        queryKey: keysFactory.supplementalLoan(creditApplicationId, financingProgram),
        queryFn: getCanHaveSupplementalLoanDto(financingProgram),
        enabled: !!creditApplicationId,
    });
    return [data ?? null, isFetching];
}
const needAssistanceTask = (financingProgram) => async ({ creditApplicationId, comment }) => {
    const response = await apiClient.post(`${financingProgram}/creditApplication/${creditApplicationId}/NeedAssistanceTask`, { comment });
    return response.data;
};
export function useNeedAssistanceTask(financingProgram) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: needAssistanceTask(financingProgram),
        onSuccess: (data) => {
            queryClient.setQueryData(keysFactory.detail(data.id, data.financingProgramId), data);
            return data;
        },
    });
}
const refreshCreditReport = async (dto) => {
    const response = await apiClient.post(`/${dto.financingProgram}/creditApplication/${encodeURIComponent(dto.creditApplicationId)}/RefreshCreditReport`, dto);
    return response.data;
};
export function useRefreshCreditReport() {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, reset } = useMutation({
        mutationFn: refreshCreditReport,
        onSuccess: (data) => {
            queryClient.setQueryData(keysFactory.detail(data.id, data.financingProgramId), data);
            return data;
        },
    });
    return [mutateAsync, isPending, reset];
}
const getFirstPaymentDateOptions = async ({ creditApplicationId, financingProgramId, firstPaymentDateOptionsDto, }) => {
    const response = await apiClient.post(`/${financingProgramId}/creditApplication/${encodeURIComponent(creditApplicationId)}/GetFirstPaymentDates`, firstPaymentDateOptionsDto, {
        params: {
            financingProgramId,
        },
    });
    return response.data;
};
export function useGetFirstPaymentDateOptions(creditApplicationId, financingProgramId, firstPaymentDateOptionsDto) {
    const { data } = useQuery({
        queryKey: [
            'firstPaymentDateOptions',
            creditApplicationId,
            financingProgramId,
            firstPaymentDateOptionsDto,
        ],
        queryFn: () => getFirstPaymentDateOptions({
            creditApplicationId: creditApplicationId,
            financingProgramId: financingProgramId,
            firstPaymentDateOptionsDto,
        }),
        enabled: !!creditApplicationId && !!financingProgramId,
    });
    return data ?? [];
}
